import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogContent,
  Skeleton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  AttachMoney,
  BarChart,
  CalendarMonth,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Close,
  Description,
  Home,
  HourglassBottom,
  IosShare,
  List as ListIcon,
  VerifiedUser,
} from '@mui/icons-material';
import { listings, mlsListings } from '@prisma/client';
import NiceModal from '@ebay/nice-modal-react';
import { useSnackbar } from 'notistack';
import { ReactReduxContext } from 'react-redux';
import { useSafeSelector } from '../../hooks/useSafeSelector';
import AssumablePropertySavings from './AssumablePropertySavings';
import MLSPropertySavings from './MLSPropertySavings';
import ClosingCostEstimator from './ClosingCostEstimator';
import ValueSummaryCard from './ValueSummaryCard';
import IDXAttribution from '../IDXAttribution';
import useIsMobile from '../../hooks/useIsMobile';
import { getStorage } from '../../utils/localOrSessionStorage';
import { trackBrowse } from '../../utils/browseAnalytics';
import { trackListingViewContent, trackStartLead } from '../../utils/metaPixel';
import mlsListingsService from '../../api/services/mlsListings';
import { liveDom } from '../../utils/liveDom';
import ScheduleTourModal from '../../pages/Dashboard/Buying/ScheduleTourModal/ScheduleTourModal';
import { MarketAnalysis } from '../BuyerPrep/MarketAnalysis';
import { RootState, AppDispatch } from '../../store/store';
import { selectShowingByMlsListingId } from '../../store/selectors/showings';
import ListingStatusChip from '../ListingStatusChip/ListingStatusChip';
import { selectMyBuyingListings } from '../../store/selectors/listings';
import { fetchListings } from '../../store/slices/listings';
import listingsService from '../../api/services/listings';
import buyerClosingCostsService from '../../api/services/buyerClosingCosts';
import {
  BuyerClosingCostsTotals,
  DEFAULT_DOWN_PAYMENT_PERCENT,
  buyerPaidClosingCosts,
  cashToCloseFor,
  closingCostQuery,
  initialPaymentMethod,
} from './closingCostQuery';
import {
  estimateBuyingPower,
  FEE_ESTIMATE_ASSUMPTIONS,
} from '../../utils/buyerBrokerFee';
import {
  isHomeSavedOnDevice,
  setHomeSavedOnDevice,
} from '../../utils/deviceSavedHomes';
import PhotoCarousel, {
  PhotoCarouselHandle,
} from './listingDialog/PhotoCarousel';
import ListingDetailRow from './listingDialog/ListingDetailRow';
import MarketPanel, {
  MarketSnapshot,
  marketSubtitle,
} from './listingDialog/MarketPanel';
import DetailsPanel, {
  ListingDetailsFields,
  detailsSummary,
} from './listingDialog/DetailsPanel';
import DocumentsPanel, {
  DocumentSummaryData,
  documentsSubtitle,
} from './listingDialog/DocumentsPanel';
import BuyingPowerCard from './listingDialog/BuyingPowerCard';
import CompactHeader from './listingDialog/CompactHeader';
import MobileActionBar from './listingDialog/MobileActionBar';
import {
  AGENT_FEE_FOOTNOTE,
  LD,
  NO_COMMITMENT_FOOTNOTE,
  formatAcres,
  formatCurrency,
} from './listingDialog/tokens';

/**
 * Where a signed-out visitor goes when they act on a listing. Signup reads
 * `mls` and creates the account with this home already on the buyer's list,
 * then lands on /buying with it waiting for a tour (PR #826), so the intent
 * that got them to sign up is not lost.
 */
const signupUrlForListing = (
  appBaseUrl: string,
  mlsListingId: string | undefined,
  shortAddress: string
): string => {
  if (!mlsListingId) return `${appBaseUrl}/signup`;
  const params = new URLSearchParams({ type: 'BUYER', mls: mlsListingId });
  if (shortAddress) params.set('address', shortAddress);
  return `${appBaseUrl}/signup?${params.toString()}`;
};

// Clamp long MLS descriptions on first render; a 1,000-character remarks
// field otherwise pushes the market read and tools two screens down on a
// phone. Short descriptions never show the toggle.
const DESCRIPTION_CLAMP_CHARS = 320;

const PHONE_HERO_HEIGHT = 300;
const DESKTOP_HERO_HEIGHT = 330;
const COMPACT_HEADER_HEIGHT = 52;

type RowKey = 'about' | 'costs' | 'market' | 'details' | 'documents';

const saveButtonLabel = (
  savedToAccount: boolean,
  isSaved: boolean,
  isSaving: boolean
): string => {
  if (savedToAccount) return 'Saved to your homes';
  if (isSaving) return 'Saving…';
  if (isSaved) return 'Saved';
  return 'Save home';
};

/** The description's opening sentence, for the desktop row's subtitle. */
const descriptionTeaser = (text: string): string => {
  const match = text.trim().match(/^[^.!?]*[.!?]?/);
  return (match?.[0] || text).trim();
};

/** "Est. $114,900 cash to close", rounded to the hundred so it reads as an estimate. */
const cashToCloseSubtitle = (
  cashToClose: number | null,
  loading: boolean
): string => {
  if (cashToClose && cashToClose > 0) {
    return `Est. ${formatCurrency(
      Math.round(cashToClose / 100) * 100
    )} cash to close`;
  }
  if (loading) return 'Estimating cash to close…';
  return 'See costs';
};

// Extended type for assumable properties that includes mortgage data
interface AssumablePropertyData extends mlsListings {
  openMortgageBalance?: number;
  assumableRate?: number;
  rateConfidence?: string;
  monthlyPaymentSavings?: number;
  tax?: number;
  photosLarge?: string[];
}

interface PropertyInfoDialogProps {
  mlsListing?: mlsListings | AssumablePropertyData;
  mlsListingId?: string;
  isAssumableOnly?: boolean;
  currentMarketRate?: number;
  onClose?: () => void;
  listing?: listings;
  isPublicPage?: boolean;
  // When provided (lake-browse only), this renders a compact lake summary block
  // at the top of the right-side advantage column, above the normal buying-power
  // content. The default /browse flow never passes it, so its behavior is
  // unchanged.
  lakeSummary?: React.ReactNode;
}

const PropertyInfoDialog: React.FC<PropertyInfoDialogProps> = ({
  mlsListing,
  mlsListingId,
  isAssumableOnly = false,
  currentMarketRate = 7.0,
  onClose,
  listing,
  isPublicPage = false,
  lakeSummary,
}) => {
  const isMobile = useIsMobile();
  const { enqueueSnackbar } = useSnackbar();
  const heroHeight = isMobile ? PHONE_HERO_HEIGHT : DESKTOP_HERO_HEIGHT;
  // Once the photo scrolls away, a compact header keeps the home's name,
  // share and close in reach. A little hysteresis stops it flickering at the
  // threshold.
  const [showCompactHeader, setShowCompactHeader] = useState(false);
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const top = e.currentTarget.scrollTop;
    const threshold = heroHeight - COMPACT_HEADER_HEIGHT;
    setShowCompactHeader((shown) =>
      shown ? top > threshold - 8 : top > threshold
    );
  };
  // Every row starts collapsed; each shows its answer in its subtitle.
  const [expandedRows, setExpandedRows] = useState<Record<RowKey, boolean>>({
    about: false,
    costs: false,
    market: false,
    details: false,
    documents: false,
  });
  const setRowExpanded = (key: RowKey, expanded: boolean) =>
    setExpandedRows((prev) => ({ ...prev, [key]: expanded }));
  const costsRowRef = useRef<HTMLDivElement | null>(null);
  const carouselRef = useRef<PhotoCarouselHandle | null>(null);
  // Redux store accessed via context (null on the provider-less marketing site)
  // so we can resolve/create the buyer listing before opening the tour modal.
  const reduxContext = useContext(ReactReduxContext);
  const [isPreparingTour, setIsPreparingTour] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [photosLoading, setPhotosLoading] = useState(true);
  // The last src the viewer's <img> finished loading. While the current src
  // differs, the previous photo is still on screen, so a spinner shows the
  // tap landed.
  const [galleryLoadedSrc, setGalleryLoadedSrc] = useState<string | null>(null);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  const [listingData, setListingData] = useState<
    mlsListings | AssumablePropertyData | null
  >(mlsListing || null);
  const [isLoading, setIsLoading] = useState(!mlsListing && !!mlsListingId);
  const [marketData, setMarketData] = useState<MarketSnapshot | null>(null);
  const [marketDataLoading, setMarketDataLoading] = useState(true);
  const [documentsProcessing, setDocumentsProcessing] = useState(false);
  const [documentSummaries, setDocumentSummaries] = useState<DocumentSummaryData[]>([]);
  const documentPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check if a showing already exists for this MLS listing
  const resolvedMlsListingId = mlsListing?.id || mlsListingId;

  // One event per home opened, once its price is known (the find() row and the
  // full get() both set listingData, so key on the id rather than the object).
  const openedMlsListingIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!resolvedMlsListingId || !listingData?.listingPrice) return;
    if (openedMlsListingIdRef.current === resolvedMlsListingId) return;
    openedMlsListingIdRef.current = resolvedMlsListingId;
    trackBrowse('browse_listing_opened', {
      mls_listing_id: resolvedMlsListingId,
      price: listingData.listingPrice,
      city: listingData.city || undefined,
      state: listingData.state || undefined,
      public_page: Boolean(isPublicPage),
    });
    trackListingViewContent({
      mlsListingId: resolvedMlsListingId,
      price: listingData.listingPrice,
      city: listingData.city || undefined,
      state: listingData.state || undefined,
    });
  }, [
    resolvedMlsListingId,
    listingData?.listingPrice,
    listingData?.city,
    listingData?.state,
    isPublicPage,
  ]);
  const existingShowing = useSafeSelector(
    (state: RootState) =>
      resolvedMlsListingId
        ? selectShowingByMlsListingId(resolvedMlsListingId)(state)
        : undefined,
    undefined
  );

  // Determine showing status for display
  const getShowingStatus = () => {
    if (!existingShowing) return null;
    // Cancelled or seller-declined (soft-deleted), allow rebooking
    if (existingShowing.accepted === null || existingShowing.cancelledAt) return null;
    const isPast = new Date(existingShowing.proposedTime) < new Date();
    if (existingShowing.accepted && isPast) return 'completed';
    if (existingShowing.accepted) return 'confirmed';
    return 'pending';
  };
  const showingStatus = getShowingStatus();
  const hasActiveShowing = showingStatus !== null;

  // A buyer's own listing row for this MLS home doubles as "saved": the tour
  // flow creates one, and Save creates the same row without opening the tour.
  const myBuyingListings = useSafeSelector(
    (state: RootState) => selectMyBuyingListings(state),
    [] as listings[]
  );
  const savedBuyerListing = resolvedMlsListingId
    ? myBuyingListings.find((l) => l.mlsListingId === resolvedMlsListingId)
    : undefined;
  const savedToAccount = Boolean(listing || savedBuyerListing);

  // Extract property details from mlsListings type
  const address = listingData?.address || '';
  const city = listingData?.city || '';
  const state = listingData?.state || '';
  const zip = listingData?.zip || '';
  const beds = listingData?.beds || 0;
  const baths = listingData?.baths || 0;
  const area = listingData?.area || 0;
  const photos = listingData?.photos || [];
  // Use large format cached images for full-screen photo gallery
  const photosLarge =
    (listingData as AssumablePropertyData)?.photosLarge || photos;
  const gallerySrc =
    photosLarge[currentPhotoIndex] || photos[currentPhotoIndex];

  // Warm the browser cache with the photos either side of the current one, so
  // a Next/Previous tap paints from cache instead of waiting on the network.
  useEffect(() => {
    if (photos.length < 2) return;
    const source = photoDialogOpen ? photosLarge : photos;
    const next = (currentPhotoIndex + 1) % photos.length;
    const prev = (currentPhotoIndex - 1 + photos.length) % photos.length;
    [next, prev].forEach((index) => {
      const src = source[index] || photos[index];
      if (src) {
        const img = new Image();
        img.decoding = 'async';
        img.src = src;
      }
    });
  }, [currentPhotoIndex, photos, photosLarge, photoDialogOpen]);
  const dom = liveDom({
    dom: listingData?.dom,
    dateListed: listingData?.dateListed,
    isActive: listingData?.isActive,
  }) ?? 0;
  const listingPrice = listingData?.listingPrice || 0;
  // For the Bargaining Power section below: scopes its market read to a
  // radius around this home (the population the comps engine actually draws
  // from) rather than the whole ZIP. Zero is the schema default for a listing
  // the feed never geocoded, not a real coordinate, so it is left undefined
  // rather than sent as a point at the equator -- MarketAnalysis/market-data
  // already treat a missing coordinate as the ZIP fallback.
  const lat = listingData?.lat || undefined;
  const lng = listingData?.lng || undefined;
  const mlsPropertyType = listingData?.propertyType || undefined;
  const listingAgentName = listingData?.listingAgentName || undefined;
  const listingAgentPhone = listingData?.listingAgentPhone || undefined;
  const listingAgentOfficePhone =
    listingData?.listingAgentOfficePhone || undefined;
  const listingOfficeName = listingData?.listingOfficeName || undefined;
  const description = listingData?.description || undefined;
  const lotSizeAcres = listingData?.lotSizeAcres || undefined;
  // Use mlsId (MLS listing number) if available, fall back to refId
  const mlsNumber = listingData?.mlsId || listingData?.refId || undefined;
  const source = listingData?.source || 'GALMLS';

  const propertyPrice = listingPrice;
  const yearBuilt =
    (listingData as AssumablePropertyData)?.yearBuilt || undefined;
  const propertyTax = (listingData as AssumablePropertyData)?.tax;
  const openMortgageBalance = (listingData as AssumablePropertyData)
    ?.openMortgageBalance;

  // Format phone number - remove "225-" prefix and format properly
  const formatPhoneNumber = (phone: string | undefined): string | undefined => {
    if (!phone) return undefined;

    // Remove "225-" prefix if present
    let cleanPhone = phone.replace(/^225-/, '');

    // Remove all non-numeric characters
    cleanPhone = cleanPhone.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX if we have 10 digits
    if (cleanPhone.length === 10) {
      return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(
        3,
        6
      )}-${cleanPhone.slice(6)}`;
    }

    // Return original if not 10 digits
    return phone;
  };

  // Helper function to get MLS attribution data
  const getMLSAttributionData = () => {
    if (!listingAgentName && !listingOfficeName) return null;

    return {
      source,
      listingFirm: listingOfficeName,
      listingAgent: listingAgentName,
      listingAgentPhone: displayPhone,
      listingId: mlsNumber,
    };
  };

  // Use formatted phone numbers
  const formattedAgentPhone = formatPhoneNumber(listingAgentPhone);
  const formattedOfficePhone = formatPhoneNumber(listingAgentOfficePhone);
  const displayPhone = formattedAgentPhone || formattedOfficePhone;

  // Buying power: the gap between a typical 3% commission and Homepoint's
  // estimated fee on this home (1.5%, $5,000 minimum, mirroring the backend).
  const buyingPowerEstimate = estimateBuyingPower(listingPrice);
  const buyingPower = buyingPowerEstimate.buyingPower;

  // An assumable-search result's own id is the assumable record's key; the
  // MLS row it points at is carried separately as mlsListingId.
  const targetMlsListingId =
    (mlsListing as { mlsListingId?: string } | undefined)?.mlsListingId ||
    mlsListing?.id ||
    mlsListingId;

  const appBaseUrl = isPublicPage ? 'https://app.homepoint.co' : '';
  const listingDeepLink = targetMlsListingId
    ? `${appBaseUrl}/browse?id=${encodeURIComponent(targetMlsListingId)}`
    : `${appBaseUrl}/browse`;

  // Save without an account: a signed-out visitor's Save stays on this
  // device. Signed in, Save is the buyer-listing row in their account.
  const isSignedIn = Boolean(getStorage().getItem('jwtToken'));
  const [savedOnDevice, setSavedOnDevice] = useState(false);
  useEffect(() => {
    setSavedOnDevice(
      targetMlsListingId ? isHomeSavedOnDevice(targetMlsListingId) : false
    );
  }, [targetMlsListingId]);
  const isSaved = savedToAccount || (!isSignedIn && savedOnDevice);
  const saveLabel = saveButtonLabel(savedToAccount, isSaved, isSaving);

  // Cash to close for the Costs row's subtitle: the same request and the same
  // arithmetic ClosingCostEstimator opens on, so the row and the panel agree.
  const [cashToClose, setCashToClose] = useState<number | null>(null);
  const [cashToCloseLoading, setCashToCloseLoading] = useState(false);
  useEffect(() => {
    if (!listingPrice) {
      setCashToClose(null);
      return undefined;
    }
    let cancelled = false;
    const paymentMethod = initialPaymentMethod(isAssumableOnly);
    setCashToCloseLoading(true);
    buyerClosingCostsService
      .find(
        closingCostQuery({
          propertyPrice: listingPrice,
          propertyTax,
          paymentMethod,
          downPaymentPercent: DEFAULT_DOWN_PAYMENT_PERCENT,
          isAssumable: isAssumableOnly,
        })
      )
      .then((data: BuyerClosingCostsTotals | undefined) => {
        if (cancelled || typeof data?.total !== 'number') return;
        setCashToClose(
          cashToCloseFor({
            propertyPrice: listingPrice,
            paymentMethod,
            downPaymentPercent: DEFAULT_DOWN_PAYMENT_PERCENT,
            isAssumable: isAssumableOnly,
            openMortgageBalance,
            closingCostsTotal: buyerPaidClosingCosts({
              total: data.total,
              otherFees: data.otherFees,
            }),
          })
        );
      })
      .catch(() => {
        if (!cancelled) setCashToClose(null);
      })
      .finally(() => {
        if (!cancelled) setCashToCloseLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [listingPrice, propertyTax, isAssumableOnly, openMortgageBalance]);

  /**
   * Resolve (or create) this buyer's own listing row for the MLS home. Both
   * the tour and Save paths need it, and reusing an existing interested-home
   * keeps repeat clicks from creating duplicates. Returns undefined when the
   * caller should hand off instead (signed out, public page, or no store).
   */
  const ensureBuyerListing = async (): Promise<listings | undefined> => {
    if (listing) return listing;

    const store = reduxContext?.store;
    if (!targetMlsListingId || !store) return undefined;

    const dispatch = store.dispatch as AppDispatch;
    const findMine = () =>
      selectMyBuyingListings(store.getState() as RootState).find(
        (l) => l.mlsListingId === targetMlsListingId
      );

    let buyerListing: listings | undefined = findMine();

    if (!buyerListing) {
      // The store may not have this buyer's listings loaded yet on /browse.
      await dispatch(fetchListings({})).unwrap();
      buyerListing = findMine();
    }

    if (!buyerListing) {
      buyerListing = (await listingsService.create(
        {
          address,
          city,
          state,
          zip,
          price: listingPrice,
          beds,
          baths,
          area,
          yearBuilt: listingData?.yearBuilt || 0,
          lat: listingData?.lat || 0,
          lng: listingData?.lng || 0,
          propertyType: listingData?.propertyType || 'SFR',
          // mlsListings.refId is the feed's own key, not a parcel id; the
          // MLS id alone identifies the home.
          mlsListingId: targetMlsListingId,
        } as Partial<listings>,
        true
      )) as listings;
      // Keep the store in sync so future clicks dedupe against this listing.
      dispatch(fetchListings({}));
    }

    return buyerListing;
  };

  /**
   * Signed-out and public-page visitors cannot act in place. Returns true
   * when it navigated away, so the caller stops.
   */
  const handOffIfCannotAct = (): boolean => {
    const token = getStorage().getItem('jwtToken');
    if (!token) {
      window.location.href = signupUrlForListing(
        appBaseUrl,
        targetMlsListingId,
        [address, city].filter(Boolean).join(', ')
      );
      return true;
    }
    // On the public marketing domain the in-app flows (Stripe, DocuSeal,
    // showings, Redux) aren't available. Hand off to the app with the
    // property so it reopens there instead of a blank dashboard.
    if (isPublicPage) {
      window.location.href = targetMlsListingId
        ? listingDeepLink
        : `${appBaseUrl}/buying`;
      return true;
    }
    return false;
  };

  // Handle tour click - centralized handler
  const handleTourClick = async () => {
    if (hasActiveShowing || isPreparingTour) return;

    const token = getStorage().getItem('jwtToken');
    trackBrowse('browse_tour_cta_clicked', {
      mls_listing_id: targetMlsListingId,
      price: listingPrice,
      city,
      state,
      authenticated: Boolean(token),
      public_page: Boolean(isPublicPage),
    });
    // The tap is the hand-raise the ad sets optimize on (StartLead), the same
    // signal the signup form's first keystroke sends.
    if (!token) trackStartLead('browse-tour-cta');

    if (handOffIfCannotAct()) return;

    // If an internal listing is already in hand, schedule the tour directly.
    if (listing) {
      NiceModal.show(ScheduleTourModal, { listing, numDays: 5 });
      return;
    }

    setIsPreparingTour(true);
    try {
      const buyerListing = await ensureBuyerListing();
      if (!buyerListing) {
        window.location.href = '/buying';
        return;
      }
      NiceModal.show(ScheduleTourModal, { listing: buyerListing, numDays: 5 });
    } catch (error) {
      console.error('Failed to start tour from MLS listing:', error);
      // Stay on the home so the buyer can retry; a redirect to /buying here
      // looked like the home had been added when it had not.
      enqueueSnackbar("We couldn't add this home. Please try again.", {
        variant: 'error',
      });
    } finally {
      setIsPreparingTour(false);
    }
  };

  // Signed out, Save toggles the home on this device. Signed in, it creates
  // the same buyer listing the tour flow does, without opening the tour
  // modal, so the home shows up under /buying.
  const handleSaveClick = async () => {
    if (savedToAccount || isSaving) return;

    if (!getStorage().getItem('jwtToken')) {
      if (!targetMlsListingId) return;
      const nowSaved = setHomeSavedOnDevice(targetMlsListingId, !savedOnDevice);
      setSavedOnDevice(nowSaved);
      if (nowSaved) {
        trackBrowse('browse_listing_saved', {
          mls_listing_id: targetMlsListingId,
          price: listingPrice,
          city,
          state,
          authenticated: false,
          public_page: Boolean(isPublicPage),
          saved_to: 'device',
        });
      }
      return;
    }
    if (handOffIfCannotAct()) return;

    setIsSaving(true);
    try {
      const buyerListing = await ensureBuyerListing();
      if (!buyerListing) {
        window.location.href = '/buying';
        return;
      }
      trackBrowse('browse_listing_saved', {
        mls_listing_id: targetMlsListingId,
        price: listingPrice,
        city,
        state,
        authenticated: true,
        public_page: Boolean(isPublicPage),
        saved_to: 'account',
      });
    } catch (error) {
      console.error('Failed to save MLS listing:', error);
      enqueueSnackbar("We couldn't save this home. Please try again.", {
        variant: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Share copies the deep link (or opens the native share sheet on phones).
  const handleShareClick = async () => {
    // listingDeepLink is already absolute on the public marketing page.
    const url = new URL(listingDeepLink, window.location.origin).toString();
    const title = address ? `${address}, ${city}` : 'Home on Homepoint';
    try {
      if (isMobile && typeof navigator.share === 'function') {
        await navigator.share({ title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      // A dismissed share sheet rejects; nothing to report.
      if ((error as { name?: string })?.name !== 'AbortError') {
        console.error('Failed to share listing link:', error);
      }
    }
  };

  // Tour button text and icon based on showing status
  const getTourButtonContent = () => {
    if (showingStatus === 'pending') return { text: 'Tour Requested', icon: <HourglassBottom /> };
    if (showingStatus === 'confirmed') return { text: 'Tour Scheduled', icon: <CheckCircle /> };
    if (showingStatus === 'completed') return { text: 'Tour Completed', icon: <CheckCircle /> };
    return null;
  };
  const showingButtonContent = getTourButtonContent();

  // Tour CTA state shared across the desktop, simple, assumable, and mobile CTAs.
  const tourButtonDisabled = hasActiveShowing || isPreparingTour;
  const tourButtonIcon = isPreparingTour ? (
    <CircularProgress size={16} color="inherit" />
  ) : (
    showingButtonContent?.icon || <CalendarMonth />
  );
  const tourButtonText = isPreparingTour
    ? 'Starting tour…'
    : showingButtonContent?.text;

  // The market read arrives from MarketAnalysis (mounted hidden so it fetches
  // with the dialog); keep what the Market row and its panel show.
  const handleMarketDataLoaded = (data: any) => {
    const conditionScores: Record<string, number> = {
      "Buyer's Market": 75,
      "Seller's Market": 25,
    };
    const score: number =
      data?.buyerAnalysis?.bargainingScore?.score ??
      conditionScores[data?.marketCondition] ??
      50;
    setMarketData({
      bargainingScore: score,
      listToSaleRatio: data?.listToSaleRatio || 0,
      medianDom: data?.medianDaysToContract || data?.medianDaysOnMarket || 0,
    });
  };

  // Keyboard navigation for the full-screen photo viewer
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      setCurrentPhotoIndex((prev) =>
        prev === 0 ? photos.length - 1 : prev - 1
      );
    } else if (e.key === 'ArrowRight') {
      setCurrentPhotoIndex((prev) =>
        prev === photos.length - 1 ? 0 : prev + 1
      );
    } else if (e.key === 'Escape' && photoDialogOpen) {
      closeViewer();
    }
  };

  // Calculate estimated monthly payment (20% down, 30yr fixed)
  const calculateMonthlyPayment = (price: number, rate = 7.0): number => {
    const downPayment = price * 0.2;
    const loanAmount = price - downPayment;
    const monthlyRate = rate / 100 / 12;
    const numPayments = 30 * 12;
    const payment =
      (loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments))) /
      (Math.pow(1 + monthlyRate, numPayments) - 1);
    return Math.round(payment);
  };

  const estimatedMonthlyPayment = propertyPrice
    ? calculateMonthlyPayment(propertyPrice, currentMarketRate)
    : 0;

  // Get ISO timestamp for IDX attribution
  const getDataTimestamp = (): string | undefined => {
    const updatedAt = listingData?.updatedAt;
    if (!updatedAt) return undefined;
    try {
      const date = new Date(updatedAt);
      if (isNaN(date.getTime())) return undefined;
      return date.toISOString();
    } catch {
      return undefined;
    }
  };

  // Handle browser back button to close the dialog
  useEffect(() => {
    if (!onClose) return;

    // Push a state so we can detect back button
    window.history.pushState({ propertyDialog: true }, '');

    const handlePopState = () => {
      onClose();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [onClose]);

  // Fetch listing data when mlsListingId is provided without mlsListing object
  // Also always fetch full listing in background to get documentSummaries and trigger document processing
  useEffect(() => {
    const abortController = new AbortController();

    // If we have mlsListing from find(), show it immediately but fetch full details in background
    if (mlsListing && mlsListing.id) {
      setIsLoading(false);
      setPhotosLoading(false);
      setHasAttemptedLoad(true);

      // Fetch full listing in background to get documentSummaries and trigger document processing
      const fetchFullListing = async () => {
        try {
          const fetchedListing = await mlsListingsService.get(
            mlsListing.id,
            abortController.signal
          );

          if (abortController.signal.aborted) return;

          if (fetchedListing) {
            // Keep the photo URLs already on screen: swapping them mid-view
            // restarts every image download for the same pictures. Map
            // results carry only the first few, so the rest are appended.
            setListingData((prev) => {
              const prevPhotos = (prev as AssumablePropertyData)?.photos;
              if (!prevPhotos || prevPhotos.length === 0) {
                return { ...prev, ...fetchedListing };
              }
              const fetchedPhotos =
                (fetchedListing as AssumablePropertyData).photos || [];
              return {
                ...prev,
                ...fetchedListing,
                photos: [
                  ...prevPhotos,
                  ...fetchedPhotos.slice(prevPhotos.length),
                ],
                photosLarge:
                  (prev as AssumablePropertyData)?.photosLarge ||
                  (fetchedListing as AssumablePropertyData).photosLarge,
              };
            });
          }
        } catch (error: any) {
          if (error?.name !== 'AbortError') {
            console.error('Error fetching full listing:', error);
          }
        }
      };

      fetchFullListing();
      return () => {
        abortController.abort();
      };
    }

    // If no mlsListingId, nothing to fetch
    if (!mlsListingId) {
      setIsLoading(false);
      setPhotosLoading(false);
      setHasAttemptedLoad(true);
      return;
    }

    const fetchListing = async () => {
      try {
        setIsLoading(true);
        setPhotosLoading(true);
        const fetchedListing = await mlsListingsService.get(
          mlsListingId,
          abortController.signal
        );

        if (abortController.signal.aborted) return;

        if (fetchedListing) {
          setListingData(fetchedListing);
        }
        setIsLoading(false);
        setPhotosLoading(false);
        setHasAttemptedLoad(true);
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.error('Error fetching listing:', error);
          setIsLoading(false);
          setPhotosLoading(false);
          setHasAttemptedLoad(true);
        }
      }
    };

    fetchListing();

    return () => {
      abortController.abort();
    };
  }, [mlsListingId, mlsListing]);

  // Sync document state from listing data and poll when processing
  useEffect(() => {
    const data = listingData as any;
    if (!data) return;

    const summaries = data.documentSummaries || [];
    setDocumentSummaries(summaries);

    const isProcessing = data.documentsProcessing === true && summaries.length === 0;
    setDocumentsProcessing(isProcessing);
  }, [listingData]);

  useEffect(() => {
    if (!documentsProcessing || !listingData?.id) {
      if (documentPollRef.current) {
        clearInterval(documentPollRef.current);
        documentPollRef.current = null;
      }
      return;
    }

    const pollForDocuments = async () => {
      try {
        const updated = await mlsListingsService.get(listingData.id);
        const updatedData = updated as any;
        const summaries = updatedData?.documentSummaries || [];
        const stillProcessing = updatedData?.documentsProcessing === true && summaries.length === 0;

        if (summaries.length > 0) {
          setDocumentSummaries(summaries);
          setDocumentsProcessing(false);
        } else if (!stillProcessing) {
          setDocumentsProcessing(false);
        }
      } catch (err) {
        console.error('Error polling for document summaries:', err);
      }
    };

    documentPollRef.current = setInterval(pollForDocuments, 5000);

    return () => {
      if (documentPollRef.current) {
        clearInterval(documentPollRef.current);
        documentPollRef.current = null;
      }
    };
  }, [documentsProcessing, listingData?.id]);

  // IDX attribution: always open, the last thing on the page. The component
  // and its props are unchanged; only the frame around it is new.
  const attributionData = getMLSAttributionData();
  const attributionBlock = attributionData ? (
    <Box
      data-testid="listing-idx-block"
      sx={{
        bgcolor: LD.subtle,
        border: `1px solid ${LD.line}`,
        borderRadius: '10px',
        px: '12px',
        py: '10px',
        '& .listing-idx': { borderTop: 'none', pt: 0, mt: 0 },
      }}
    >
      {isMobile && (
        <Typography
          sx={{
            fontSize: '11.5px',
            fontWeight: 700,
            color: LD.ink2,
            lineHeight: 1.5,
          }}
        >
          Listing information
        </Typography>
      )}
      <IDXAttribution
        className="listing-idx"
        source={attributionData.source}
        listingFirm={attributionData.listingFirm}
        listingAgent={attributionData.listingAgent}
        listingAgentPhone={attributionData.listingAgentPhone}
        listingId={attributionData.listingId}
        dataTimestamp={getDataTimestamp()}
        compact={isMobile}
        showDisclaimer={true}
      />
    </Box>
  ) : null;

  // Show skeleton loading state when fetching listing by mlsListingId
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          bgcolor: 'white',
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="Close dialog"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1100,
            // 44x44 tap target (icon stays 24px; the extra space is padding) so
            // the close control clears the touch floor on the fullscreen dialog.
            width: 44,
            height: 44,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.7)',
            },
          }}
        >
          <Close />
        </IconButton>
        {/* Photo skeleton */}
        <Skeleton
          variant="rectangular"
          width="100%"
          height={heroHeight}
          animation="wave"
        />
        {/* Content skeleton */}
        <Box sx={{ p: isMobile ? 2 : 3 }}>
          <Skeleton variant="text" width="40%" height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="60%" height={24} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" width="100%" height={72} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="75%" />
        </Box>
      </Box>
    );
  }

  const hasBuyingPower = !isAssumableOnly && buyingPower > 0;
  const tourCtaText =
    tourButtonText ||
    (buyingPower > 0
      ? `See Home & Use ${formatCurrency(buyingPower)}`
      : 'Tour with Homepoint');
  const fullAddress = [address, city, [state, zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  const factParts = [
    `${beds || 0} bd`,
    `${baths || 0} ba`,
    `${area ? area.toLocaleString() : 'N/A'} sqft`,
  ];
  if (lotSizeAcres && lotSizeAcres > 0) {
    factParts.push(`${formatAcres(lotSizeAcres)} ac`);
  }
  const metaParts: string[] = [];
  if (yearBuilt) metaParts.push(`Built ${yearBuilt}`);
  if (!isAssumableOnly && dom > 0) metaParts.push(`${dom} days on market`);
  if (mlsNumber) metaParts.push(`MLS# ${mlsNumber}`);
  const monthlyText =
    estimatedMonthlyPayment > 0
      ? `Est. ${formatCurrency(estimatedMonthlyPayment)}/mo`
      : '';
  const detailsFields = listingData as unknown as ListingDetailsFields;
  const detailsParts = detailsSummary(detailsFields || {});
  const nearbyMarket = lat !== undefined && lng !== undefined;
  const costsTitle = isAssumableOnly
    ? 'Rate savings and costs'
    : 'Costs and buying power';

  const priceText = propertyPrice
    ? formatCurrency(propertyPrice)
    : 'Contact for Price';

  const facts = (
    <Box
      component="span"
      data-testid="listing-facts"
      sx={{ fontSize: '16px', fontWeight: 700, color: LD.ink }}
    >
      {factParts.map((part, index) => (
        <React.Fragment key={part}>
          {index > 0 && (
            <Box component="span" sx={{ color: LD.factDot, mx: '5px' }}>
              •
            </Box>
          )}
          {part}
        </React.Fragment>
      ))}
    </Box>
  );

  const statusChip = (
    <Box sx={{ display: 'flex', '&:not(:empty)': { mt: '6px' } }}>
      <ListingStatusChip
        status={listingData?.isActive}
        mlsStatus={
          (listingData as { mlsStatus?: string | null } | null)?.mlsStatus
        }
      />
    </Box>
  );

  // Opens the Costs row and brings it into view below the compact header.
  const openCostsRow = () => {
    setRowExpanded('costs', true);
    window.setTimeout(() => {
      costsRowRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 50);
  };

  // Closing the viewer leaves the strip on the photo the visitor ended on.
  const closeViewer = () => {
    setPhotoDialogOpen(false);
    carouselRef.current?.scrollToIndex(currentPhotoIndex);
  };

  const shareButton = (
    <Tooltip
      title="Link copied"
      open={linkCopied}
      disableHoverListener
      disableFocusListener
      disableTouchListener
      placement="bottom"
    >
      <IconButton
        onClick={handleShareClick}
        aria-label="Share this home"
        sx={overlayButtonSx}
      >
        <Box component="span" sx={overlayCircleSx}>
          <IosShare sx={{ fontSize: 19 }} />
        </Box>
      </IconButton>
    </Tooltip>
  );

  // Share + Close over the photo. Each is a 44x44 tap target around the
  // design's 38px circle, so both clear the touch floor.
  const heroOverlay = (
    <Box
      sx={{
        position: 'absolute',
        top: 7,
        right: 7,
        zIndex: 4,
        display: 'flex',
        gap: '2px',
      }}
    >
      {shareButton}
      {onClose && (
        <IconButton
          onClick={onClose}
          aria-label="Close property details"
          sx={overlayButtonSx}
        >
          <Box component="span" sx={overlayCircleSx}>
            <Close sx={{ fontSize: 21 }} />
          </Box>
        </IconButton>
      )}
    </Box>
  );

  let hero: React.ReactNode;
  if (photosLoading || (!hasAttemptedLoad && photos.length === 0)) {
    hero = (
      <Box sx={{ position: 'relative', height: heroHeight, flexShrink: 0 }}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
        />
        {heroOverlay}
      </Box>
    );
  } else if (photos.length > 0) {
    hero = (
      <PhotoCarousel
        ref={carouselRef}
        photos={photos}
        variant={isMobile ? 'phone' : 'desktop'}
        index={currentPhotoIndex}
        onIndexChange={setCurrentPhotoIndex}
        onOpenViewer={(index) => {
          setCurrentPhotoIndex(index);
          setPhotoDialogOpen(true);
        }}
        overlay={heroOverlay}
      />
    );
  } else {
    hero = (
      <Box
        sx={{
          position: 'relative',
          height: heroHeight,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f3f4f6',
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No photos available
        </Typography>
        {heroOverlay}
      </Box>
    );
  }

  // Costs row body: how the buying power is figured, the calculator, and the
  // closing-cost breakdown. Rendered on first open.
  const costsBody = (
    <Box>
      <Box sx={{ px: isMobile ? 2 : 0, pb: '10px' }}>
        {monthlyText && (
          <Typography
            data-testid="monthly-estimate-assumptions"
            sx={{ fontSize: '12.5px', color: LD.muted, lineHeight: 1.5 }}
          >
            {monthlyText} assumes 20% down · {currentMarketRate.toFixed(1)}% ·
            30-yr · principal and interest.
          </Typography>
        )}
        {!isAssumableOnly && buyingPower > 0 && (
          <Typography
            sx={{
              fontSize: '12.5px',
              color: LD.muted,
              lineHeight: 1.5,
              mt: '4px',
            }}
          >
            {FEE_ESTIMATE_ASSUMPTIONS}
          </Typography>
        )}
      </Box>
      {isAssumableOnly && listingData && (
        <AssumablePropertySavings
          property={listingData as AssumablePropertyData}
          currentMarketRate={currentMarketRate}
        />
      )}
      {!isAssumableOnly && listingData && buyingPower > 0 && (
        <MLSPropertySavings
          property={listingData}
          showCalculatorByDefault={true}
          embedded={true}
        />
      )}
      <ClosingCostEstimator
        propertyPrice={propertyPrice}
        propertyTax={propertyTax}
        address={address}
        isAssumable={isAssumableOnly}
        openMortgageBalance={openMortgageBalance}
      />
      <Typography
        sx={{
          fontSize: '11px',
          color: LD.muted2,
          mt: '8px',
          px: isMobile ? 2 : 0,
        }}
      >
        Provided by Homepoint
      </Typography>
    </Box>
  );

  // The market read is keyed on the ZIP; a listing without one never fetches,
  // so it is not loading either.
  const marketLoading = Boolean(zip) && marketDataLoading;

  const marketBody = (
    <Box sx={{ px: isMobile ? 2 : 0 }}>
      <MarketPanel
        snapshot={marketData}
        loading={marketLoading}
        dom={dom}
        zip={zip}
        nearby={nearbyMarket}
      />
    </Box>
  );

  // Fetches the market read with the dialog so the Market row's subtitle is
  // ready before anyone opens it; MarketPanel draws what it reports.
  const marketSource = zip ? (
    <Box sx={{ display: 'none' }} aria-hidden>
      <MarketAnalysis
        zip={zip}
        dom={dom}
        listingPrice={listingPrice}
        area={area}
        lat={lat}
        lng={lng}
        state={state}
        propertyType={mlsPropertyType}
        status={listingData?.isActive || undefined}
        compact={true}
        onDataLoaded={handleMarketDataLoaded}
        onLoadingChange={setMarketDataLoading}
      />
    </Box>
  ) : null;

  const descriptionText =
    description &&
    (descriptionExpanded || description.length <= DESCRIPTION_CLAMP_CHARS
      ? description
      : `${description.slice(0, DESCRIPTION_CLAMP_CHARS).trimEnd()}…`);

  const phoneBody = (
    <>
      <Box
        sx={{
          px: 2,
          pt: '14px',
          pb: '12px',
          borderBottom: `1px solid ${LD.line}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 1,
          }}
        >
          <Typography
            component="span"
            data-testid="listing-price"
            sx={{
              fontSize: '28px',
              fontWeight: 800,
              color: LD.primary,
              letterSpacing: '-0.01em',
              lineHeight: 'normal',
            }}
          >
            {priceText}
          </Typography>
          {monthlyText && (
            <Typography
              component="span"
              data-testid="monthly-estimate"
              sx={{ fontSize: '12.5px', color: LD.muted }}
            >
              {monthlyText}
            </Typography>
          )}
        </Box>
        <Typography
          data-testid="listing-address"
          sx={{
            fontSize: '15px',
            color: LD.ink2,
            mt: '2px',
            lineHeight: 'normal',
          }}
        >
          {fullAddress}
        </Typography>
        <Box sx={{ mt: '8px', lineHeight: 'normal' }}>{facts}</Box>
        {metaParts.length > 0 && (
          <Typography
            sx={{
              fontSize: '13px',
              color: LD.muted,
              mt: '3px',
              lineHeight: 'normal',
            }}
          >
            {metaParts.join(' · ')}
          </Typography>
        )}
        {statusChip}
        {hasBuyingPower && (
          <BuyingPowerCard
            estimate={buyingPowerEstimate}
            onHowItWorks={openCostsRow}
          />
        )}
        {/* A visitor from a listing ad has never heard of Homepoint: name the
            brokerage before the sticky bar asks for anything. Worded as the
            buyer's side, with no face, so it can't be read as the listing
            agent (GALMLS 16.20, 18.3.4); the listing firm and agent stay in
            the IDX block. */}
        <Box
          data-testid="mobile-brokerage-line"
          sx={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            mt: '10px',
            fontSize: '12.5px',
            color: LD.ink2,
            lineHeight: 'normal',
          }}
        >
          <VerifiedUser sx={{ fontSize: 18, color: LD.navy, flexShrink: 0 }} />
          <span>
            <b>Homepoint is a licensed brokerage.</b> Tour and buy with a
            full-service agent.
          </span>
        </Box>
        {lakeSummary && <Box sx={{ mt: '12px' }}>{lakeSummary}</Box>}
        {descriptionText && (
          <>
            <Typography
              sx={{
                mt: '16px',
                fontSize: '12px',
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: LD.muted,
              }}
            >
              About this home
            </Typography>
            <Typography
              component="div"
              sx={{
                mt: '4px',
                fontSize: '14.5px',
                lineHeight: 1.55,
                color: LD.ink2,
                whiteSpace: 'pre-line',
              }}
            >
              <span data-testid="listing-description">{descriptionText}</span>
              {description && description.length > DESCRIPTION_CLAMP_CHARS && (
                <>
                  {' '}
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setDescriptionExpanded((v) => !v)}
                    aria-expanded={descriptionExpanded}
                    sx={inlineLinkSx}
                  >
                    {descriptionExpanded ? 'Show less' : 'Read more'}
                  </Box>
                </>
              )}
            </Typography>
          </>
        )}
      </Box>

      <Box
        ref={costsRowRef}
        sx={{ scrollMarginTop: `${COMPACT_HEADER_HEIGHT}px` }}
      >
        <ListingDetailRow
          testId="row-costs"
          icon={<AttachMoney />}
          title={costsTitle}
          subtitle={cashToCloseSubtitle(cashToClose, cashToCloseLoading)}
          expanded={expandedRows.costs}
          onChange={(open) => setRowExpanded('costs', open)}
          lazy
        >
          {costsBody}
        </ListingDetailRow>
      </Box>
      <ListingDetailRow
        testId="row-market"
        icon={<BarChart />}
        title="Market"
        subtitle={marketSubtitle(marketData, marketLoading)}
        expanded={expandedRows.market}
        onChange={(open) => setRowExpanded('market', open)}
      >
        {marketBody}
      </ListingDetailRow>
      <ListingDetailRow
        testId="row-details"
        icon={<ListIcon />}
        title="Details"
        subtitle={detailsParts.join(' · ') || 'Property facts'}
        expanded={expandedRows.details}
        onChange={(open) => setRowExpanded('details', open)}
        lazy
      >
        <Box sx={{ px: 2 }}>
          <DetailsPanel
            fields={detailsFields || {}}
            dom={dom}
            mlsNumber={mlsNumber}
          />
        </Box>
      </ListingDetailRow>
      <ListingDetailRow
        testId="row-documents"
        icon={<Description />}
        title="Documents"
        subtitle={documentsSubtitle(documentSummaries.length)}
        expanded={expandedRows.documents}
        onChange={(open) => setRowExpanded('documents', open)}
        lazy
      >
        <Box sx={{ px: 2 }}>
          <DocumentsPanel documentSummaries={documentSummaries} />
        </Box>
      </ListingDetailRow>

      {/* Bottom padding keeps the IDX block clear of the sticky tour bar. */}
      <Box sx={{ px: 2, pt: '12px', pb: '152px' }}>{attributionBlock}</Box>
    </>
  );

  let desktopAction: React.ReactNode;
  if (isAssumableOnly && listingData) {
    desktopAction = (
      <Box
        sx={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: `1px solid ${LD.line}`,
        }}
      >
        <AssumablePropertySavings
          property={listingData as AssumablePropertyData}
          currentMarketRate={currentMarketRate}
        />
        <Box sx={{ p: 2, borderTop: `1px solid ${LD.line}` }}>
          <ValueSummaryCard
            propertyPrice={0}
            onTourClick={handleTourClick}
            tourButtonText={tourButtonText || 'Tour This Home'}
            tourButtonIcon={tourButtonIcon}
            tourButtonDisabled={tourButtonDisabled}
          />
        </Box>
      </Box>
    );
  } else if (!isAssumableOnly) {
    desktopAction = (
      <ValueSummaryCard
        propertyPrice={listingPrice}
        onTourClick={handleTourClick}
        onSaveClick={handleSaveClick}
        isSaved={isSaved}
        isSaving={isSaving}
        saveLabel={saveLabel}
        saveLocked={savedToAccount}
        tourButtonText={tourButtonText}
        tourButtonIcon={tourButtonIcon}
        tourButtonDisabled={tourButtonDisabled}
      />
    );
  }

  const desktopSubline = [
    area && propertyPrice
      ? `${formatCurrency(Math.round(propertyPrice / area))}/sqft`
      : '',
    monthlyText,
  ]
    .filter(Boolean)
    .join(' · ');

  const desktopBody = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 370px',
      }}
    >
      <Box
        sx={{
          px: '26px',
          py: '18px',
          borderRight: `1px solid ${LD.line}`,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="span"
              data-testid="listing-price"
              sx={{
                fontSize: '32px',
                fontWeight: 800,
                color: LD.primary,
                letterSpacing: '-0.01em',
                lineHeight: 'normal',
              }}
            >
              {priceText}
            </Typography>
            {desktopSubline && (
              <Typography
                component="span"
                data-testid="monthly-estimate"
                sx={{
                  fontSize: '15px',
                  color: LD.muted,
                  fontWeight: 500,
                  ml: '8px',
                }}
              >
                {desktopSubline}
              </Typography>
            )}
          </Box>
          <Typography
            data-testid="listing-address"
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              color: LD.ink,
              textAlign: 'right',
            }}
          >
            {fullAddress}
          </Typography>
        </Box>
        <Box sx={{ mt: '6px', lineHeight: 'normal' }}>
          {facts}
          {metaParts.length > 0 && (
            <Box
              component="span"
              sx={{
                fontSize: '13px',
                color: LD.muted,
                fontWeight: 500,
                ml: '8px',
              }}
            >
              {metaParts.join(' · ')}
            </Box>
          )}
        </Box>
        {statusChip}

        <Box sx={{ mt: '10px', borderTop: `1px solid ${LD.line}` }}>
          {description && (
            <ListingDetailRow
              testId="row-about"
              inset={false}
              icon={<Home />}
              title="About this home"
              subtitle={descriptionTeaser(description)}
              expanded={expandedRows.about}
              onChange={(open) => setRowExpanded('about', open)}
            >
              <Typography
                data-testid="listing-description"
                sx={{
                  fontSize: '14.5px',
                  lineHeight: 1.55,
                  color: LD.ink2,
                  whiteSpace: 'pre-line',
                }}
              >
                {description}
              </Typography>
            </ListingDetailRow>
          )}
          <ListingDetailRow
            testId="row-costs"
            inset={false}
            icon={<AttachMoney />}
            title={costsTitle}
            subtitle={cashToCloseSubtitle(cashToClose, cashToCloseLoading)}
            expanded={expandedRows.costs}
            onChange={(open) => setRowExpanded('costs', open)}
            lazy
          >
            {costsBody}
          </ListingDetailRow>
          <ListingDetailRow
            testId="row-market"
            inset={false}
            icon={<BarChart />}
            title="Market"
            subtitle={marketSubtitle(marketData, marketLoading)}
            expanded={expandedRows.market}
            onChange={(open) => setRowExpanded('market', open)}
          >
            {marketBody}
          </ListingDetailRow>
          <ListingDetailRow
            testId="row-details"
            inset={false}
            icon={<Description />}
            title="Details and documents"
            subtitle={[
              ...detailsParts,
              documentSummaries.length > 0
                ? `${documentSummaries.length} documents`
                : 'no documents yet',
            ].join(' · ')}
            expanded={expandedRows.details}
            onChange={(open) => setRowExpanded('details', open)}
            lazy
          >
            <DetailsPanel
              fields={detailsFields || {}}
              dom={dom}
              mlsNumber={mlsNumber}
            />
            <Box sx={{ mt: '12px' }}>
              <DocumentsPanel documentSummaries={documentSummaries} />
            </Box>
          </ListingDetailRow>
        </Box>

        {attributionBlock && <Box sx={{ mt: '16px' }}>{attributionBlock}</Box>}
      </Box>

      {/* The action card holds its place while the page scrolls; it never
          scrolls on its own. */}
      <Box sx={{ px: '22px', py: '18px' }}>
        <Box
          sx={{
            position: 'sticky',
            top: COMPACT_HEADER_HEIGHT + 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {lakeSummary}
          {desktopAction}
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <Box
        onScroll={handleScroll}
        sx={{
          width: '100%',
          height: '100%',
          bgcolor: 'white',
          overflowX: 'hidden',
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        {showCompactHeader && (
          <Box sx={{ position: 'sticky', top: 0, height: 0, zIndex: 20 }}>
            <CompactHeader
              thumbnail={photos[0]}
              street={address || fullAddress}
              summary={[priceText, `${beds || 0} bd`, `${baths || 0} ba`].join(
                ' · '
              )}
              onShare={handleShareClick}
              onClose={onClose}
            />
          </Box>
        )}
        {hero}
        {isMobile ? phoneBody : desktopBody}
        {marketSource}
      </Box>

      {/* Photo Gallery Dialog */}
      <Dialog
        open={photoDialogOpen}
        onClose={closeViewer}
        maxWidth="lg"
        fullWidth
        aria-label="Photo gallery"
        PaperProps={{
          sx: {
            bgcolor: 'black',
            m: 0,
          },
        }}
        onKeyDown={handleKeyDown}
      >
        <IconButton
          onClick={closeViewer}
          aria-label="Close photo gallery"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            bgcolor: 'rgba(0,0,0,0.5)',
            zIndex: 1,
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.7)',
            },
          }}
        >
          <Close />
        </IconButton>

        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            alignItems: 'center',
            minHeight: '80vh',
          }}
        >
          {photos.length > 0 && (
            <Box sx={{ position: 'relative', width: '100%' }}>
              <img
                src={gallerySrc}
                decoding="async"
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                }}
                onLoad={(e) =>
                  setGalleryLoadedSrc(e.currentTarget.getAttribute('src'))
                }
                alt={`Property photo ${currentPhotoIndex + 1} of ${
                  photos.length
                }`}
              />
              {galleryLoadedSrc !== gallerySrc && (
                <Box
                  role="status"
                  aria-label="Loading photo"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'transparent',
                    pointerEvents: 'none',
                  }}
                >
                  <CircularProgress size={32} />
                </Box>
              )}

              {photos.length > 1 && (
                <>
                  <IconButton
                    onClick={() =>
                      setCurrentPhotoIndex((prev) =>
                        prev === 0 ? photos.length - 1 : prev - 1
                      )
                    }
                    aria-label="Previous photo"
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                    }}
                  >
                    <ChevronLeft fontSize="large" />
                  </IconButton>

                  <IconButton
                    onClick={() =>
                      setCurrentPhotoIndex((prev) =>
                        prev === photos.length - 1 ? 0 : prev + 1
                      )
                    }
                    aria-label="Next photo"
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                    }}
                  >
                    <ChevronRight fontSize="large" />
                  </IconButton>

                  <Typography
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      color: 'white',
                      bgcolor: 'rgba(0,0,0,0.5)',
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                    }}
                    aria-live="polite"
                  >
                    {currentPhotoIndex + 1} / {photos.length}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Sticky Bottom CTA Bar - Mobile Only */}

      {isMobile && (
        <MobileActionBar
          ctaText={tourCtaText}
          ctaIcon={tourButtonIcon}
          ctaDisabled={tourButtonDisabled}
          onCtaClick={handleTourClick}
          onSaveClick={isAssumableOnly ? undefined : handleSaveClick}
          isSaved={isSaved}
          saveDisabled={isSaving || savedToAccount}
          saveLabel={saveLabel}
          footnote={
            hasBuyingPower ? AGENT_FEE_FOOTNOTE : NO_COMMITMENT_FOOTNOTE
          }
        />
      )}
    </>
  );
};

const overlayButtonSx = {
  width: 44,
  height: 44,
  p: 0,
  color: 'white',
  '&:hover > span': { bgcolor: 'rgba(17, 24, 39, 0.72)' },
};

const overlayCircleSx = {
  width: 38,
  height: 38,
  borderRadius: '50%',
  bgcolor: LD.overlay,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const inlineLinkSx = {
  p: 0,
  border: 0,
  bgcolor: 'transparent',
  font: 'inherit',
  color: LD.primary,
  fontWeight: 700,
  cursor: 'pointer',
};

export default PropertyInfoDialog;
